import { EventBus } from "../EventBus.js";
import { pushFeed } from "../Feed.js";

/**
 * Difficulty — a crude five-step scale from Peace to Brutal, runtime-
 * toggleable (MainMenu slider, `?difficulty=` / `?peace=1` boot params).
 *
 * The scale turns exactly two knobs, both enemy-side, so progression
 * tuning (XP, loot, player math) is never warped:
 *
 * - `enemyDamageScale` — multiplies rolled enemy damage (min 1 on hit).
 * - `enemyCooldownScale` — multiplies the enemy ATB cooldown (bigger =
 *   slower attacks).
 *
 * **Peace** is the special floor: enemies never detect, chase,
 * pack-aggro, or attack (see `CombatManager`'s `isPeaceMode()` gates);
 * they still patrol, block cells, and die to player attacks, so boss
 * `death_knot`s, spawners, and `requires_boss_dead` gates stay fully
 * testable while walking maps and checking Ink knots.
 *
 * Runtime-only state — never serialized into saves (a per-save
 * difficulty is a future persistence-contract change).
 */
export type DifficultyLevel = "peace" | "story" | "normal" | "hard" | "brutal";

export const DIFFICULTY_ORDER: readonly DifficultyLevel[] = [
  "peace",
  "story",
  "normal",
  "hard",
  "brutal",
];

export interface DifficultyTuning {
  readonly label: string;
  /** Multiplier on rolled enemy damage. Irrelevant at peace (no attacks). */
  readonly enemyDamageScale: number;
  /** Multiplier on the enemy ATB cooldown — bigger = slower attacks. */
  readonly enemyCooldownScale: number;
  readonly feedLine: string;
}

export const DIFFICULTY_TUNING: Record<DifficultyLevel, DifficultyTuning> = {
  peace: {
    label: "Peace",
    enemyDamageScale: 0,
    enemyCooldownScale: 1,
    feedLine: "Peace mode on — the world stands down.",
  },
  story: {
    label: "Story",
    enemyDamageScale: 0.5,
    enemyCooldownScale: 1.5,
    feedLine: "Difficulty: Story — enemies hit softer and slower.",
  },
  normal: {
    label: "Normal",
    enemyDamageScale: 1,
    enemyCooldownScale: 1,
    feedLine: "Difficulty: Normal.",
  },
  hard: {
    label: "Hard",
    enemyDamageScale: 1.5,
    enemyCooldownScale: 0.75,
    feedLine: "Difficulty: Hard — enemies hit harder and faster.",
  },
  brutal: {
    label: "Brutal",
    enemyDamageScale: 2,
    enemyCooldownScale: 0.6,
    feedLine: "Difficulty: Brutal. Good luck.",
  },
};

let level: DifficultyLevel = "normal";

/**
 * Per-adventure tuning curve — the manifest's `difficulty` block. A
 * five-step global scale can't fit every adventure (exit-velocity's
 * normal ran far hotter than eob-mini's), so adventures may re-anchor
 * any level's knobs; unspecified levels keep the global table. Applied
 * by `setActiveAdventure`, cleared on adventure reset.
 */
export type DifficultyOverrides = Partial<
  Record<DifficultyLevel, Partial<Pick<DifficultyTuning, "enemyDamageScale" | "enemyCooldownScale">>>
>;

let adventureOverrides: DifficultyOverrides = {};

export function setAdventureDifficultyOverrides(overrides: DifficultyOverrides | undefined): void {
  adventureOverrides = overrides ?? {};
}

function effectiveTuning(l: DifficultyLevel): Pick<DifficultyTuning, "enemyDamageScale" | "enemyCooldownScale"> {
  const base = DIFFICULTY_TUNING[l];
  const over = adventureOverrides[l];
  return {
    enemyDamageScale: over?.enemyDamageScale ?? base.enemyDamageScale,
    enemyCooldownScale: over?.enemyCooldownScale ?? base.enemyCooldownScale,
  };
}

export function getDifficulty(): DifficultyLevel {
  return level;
}

export function setDifficulty(next: DifficultyLevel): void {
  if (level === next) return;
  level = next;
  const tuning = DIFFICULTY_TUNING[next];
  EventBus.emit("combat:difficulty_changed", { level: next, label: tuning.label });
  pushFeed(tuning.feedLine);
}

/** True at the Peace floor — CombatManager gates all enemy aggression on this. */
export function isPeaceMode(): boolean {
  return level === "peace";
}

/** Scale a rolled enemy damage total; hits always deal at least 1. */
export function scaleEnemyDamage(total: number): number {
  return Math.max(1, Math.round(total * effectiveTuning(level).enemyDamageScale));
}

/** Scale the enemy ATB cooldown for the current difficulty. */
export function scaleEnemyCooldown(baseSeconds: number): number {
  return baseSeconds * effectiveTuning(level).enemyCooldownScale;
}

/**
 * Parse a boot-param value: a level name (`"story"`) or a scale index
 * (`"0"`–`"4"`). Returns null on anything unrecognized.
 */
export function parseDifficulty(value: string): DifficultyLevel | null {
  const lowered = value.toLowerCase();
  if ((DIFFICULTY_ORDER as readonly string[]).includes(lowered)) {
    return lowered as DifficultyLevel;
  }
  const idx = Number(lowered);
  if (Number.isInteger(idx)) {
    const byIndex = DIFFICULTY_ORDER[idx];
    if (byIndex) return byIndex;
  }
  return null;
}
